import React, { useState } from 'react';

const ProposedPagesModal = ({ isOpen, onClose, proposedPages }) => {
    const [expandedPages, setExpandedPages] = useState({});
    const [filterPriority, setFilterPriority] = useState('all');
    const [searchQuery, setSearchQuery] = useState('');

    if (!isOpen || !proposedPages || !Array.isArray(proposedPages.proposed_pages)) {
        return null;
    }

    const togglePage = (pageIndex) => {
        setExpandedPages(prev => ({
            ...prev,
            [pageIndex]: !prev[pageIndex]
        }));
    };

    const filteredPages = proposedPages.proposed_pages.filter(page => {
        const matchesPriority = filterPriority === 'all' || page.priority === filterPriority;
        const matchesSearch = !searchQuery || 
            page.page_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            page.purpose?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            page.page_url?.toLowerCase().includes(searchQuery.toLowerCase());
        return matchesPriority && matchesSearch;
    });

    const modalOverlayStyle = {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.85)',
        zIndex: 10000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
        overflow: 'auto'
    };

    const modalContentStyle = {
        backgroundColor: '#1f1f1f',
        borderRadius: '16px',
        padding: '30px',
        maxWidth: '1400px',
        width: '100%',
        maxHeight: '90vh',
        overflowY: 'auto',
        border: '1px solid rgba(139, 92, 246, 0.3)',
        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)'
    };

    const headerStyle = {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '30px',
        paddingBottom: '20px',
        borderBottom: '1px solid rgba(139, 92, 246, 0.2)'
    };

    const titleStyle = {
        fontSize: '24px',
        fontWeight: '700',
        color: '#ffffff',
        margin: 0
    };

    const closeButtonStyle = {
        backgroundColor: 'transparent',
        border: 'none',
        color: '#9a9a9a',
        fontSize: '24px',
        cursor: 'pointer',
        padding: '0',
        width: '32px',
        height: '32px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: '6px',
        transition: 'all 0.2s ease'
    };

    const filtersStyle = {
        display: 'flex',
        gap: '12px',
        marginBottom: '20px',
        flexWrap: 'wrap',
        alignItems: 'center'
    };

    const inputStyle = {
        backgroundColor: '#2d2d2d',
        border: '1px solid #404040',
        borderRadius: '8px',
        padding: '10px 14px',
        fontSize: '14px',
        color: '#e0e0e0',
        flex: '1',
        minWidth: '200px'
    };

    const buttonStyle = {
        backgroundColor: '#2d2d2d',
        border: '1px solid #404040',
        borderRadius: '8px',
        padding: '8px 16px',
        fontSize: '13px',
        color: '#e0e0e0',
        cursor: 'pointer',
        transition: 'all 0.2s ease'
    };

    const buttonActiveStyle = {
        backgroundColor: '#8b5cf6',
        border: '1px solid #8b5cf6',
        color: '#ffffff'
    };

    const pageCardStyle = {
        background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.12), rgba(139, 92, 246, 0.04))',
        border: '1px solid rgba(139, 92, 246, 0.25)',
        borderRadius: '12px',
        padding: '20px',
        marginBottom: '16px',
        cursor: 'pointer',
        transition: 'all 0.2s ease'
    };

    const pageCardExpandedStyle = {
        background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.18), rgba(139, 92, 246, 0.08))',
        border: '1px solid rgba(139, 92, 246, 0.4)'
    };

    const pageHeaderStyle = {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        marginBottom: '12px'
    };

    const pageTitleStyle = {
        fontSize: '16px',
        fontWeight: '600',
        color: '#ffffff',
        margin: 0,
        flex: 1
    };

    const badgeStyle = (priority) => {
        const colors = {
            critical: { bg: '#ef4444', text: '#ffffff' },
            high: { bg: '#f59e0b', text: '#ffffff' },
            important: { bg: '#3b82f6', text: '#ffffff' },
            medium: { bg: '#6b7280', text: '#ffffff' },
            low: { bg: '#4b5563', text: '#ffffff' }
        };
        const color = colors[priority] || colors.medium;
        return {
            backgroundColor: color.bg,
            color: color.text,
            padding: '4px 10px',
            borderRadius: '6px',
            fontSize: '11px',
            fontWeight: '600',
            marginLeft: '12px'
        };
    };

    const labelStyle = {
        fontSize: '12px',
        color: '#9a9a9a',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        marginBottom: '6px',
        fontWeight: '600'
    };

    const valueStyle = {
        fontSize: '14px',
        color: '#e0e0e0',
        lineHeight: '1.6',
        wordBreak: 'break-word',
        marginBottom: '12px'
    };

    const subsectionStyle = {
        backgroundColor: '#2d2d2d',
        border: '1px solid #404040',
        borderRadius: '8px',
        padding: '12px',
        marginBottom: '12px'
    };

    const getPriorityCounts = () => {
        const counts = { all: proposedPages.proposed_pages.length };
        proposedPages.proposed_pages.forEach(page => {
            const priority = page.priority || 'other';
            counts[priority] = (counts[priority] || 0) + 1;
        });
        return counts;
    };

    const priorityCounts = getPriorityCounts();

    return (
        <div style={modalOverlayStyle} onClick={onClose}>
            <div style={modalContentStyle} onClick={(e) => e.stopPropagation()}>
                <div style={headerStyle}>
                    <div>
                        <h2 style={titleStyle}>Proposed Pages</h2>
                        {proposedPages.summary && (
                            <div style={{ fontSize: '14px', color: '#9a9a9a', marginTop: '8px' }}>
                                {proposedPages.summary.total_pages || proposedPages.proposed_pages.length} pages total
                                {proposedPages.summary.critical_pages && ` • ${proposedPages.summary.critical_pages} critical`}
                                {proposedPages.summary.high_priority_pages && ` • ${proposedPages.summary.high_priority_pages} high priority`}
                            </div>
                        )}
                    </div>
                    <button
                        style={closeButtonStyle}
                        onClick={onClose}
                        onMouseEnter={(e) => {
                            e.target.style.backgroundColor = '#2d2d2d';
                            e.target.style.color = '#ffffff';
                        }}
                        onMouseLeave={(e) => {
                            e.target.style.backgroundColor = 'transparent';
                            e.target.style.color = '#9a9a9a';
                        }}
                    >
                        ×
                    </button>
                </div>

                {/* Filters */}
                <div style={filtersStyle}>
                    <input
                        type="text"
                        placeholder="Search pages..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        style={inputStyle}
                    />
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                        {['all', 'critical', 'high', 'important', 'medium', 'low'].map(priority => (
                            <button
                                key={priority}
                                style={{
                                    ...buttonStyle,
                                    ...(filterPriority === priority ? buttonActiveStyle : {})
                                }}
                                onClick={() => setFilterPriority(priority)}
                                onMouseEnter={(e) => {
                                    if (filterPriority !== priority) {
                                        e.target.style.backgroundColor = '#3d3d3d';
                                    }
                                }}
                                onMouseLeave={(e) => {
                                    if (filterPriority !== priority) {
                                        e.target.style.backgroundColor = '#2d2d2d';
                                    }
                                }}
                            >
                                {priority.charAt(0).toUpperCase() + priority.slice(1)} ({priorityCounts[priority] || 0})
                            </button>
                        ))}
                    </div>
                </div>

                {/* Pages List */}
                <div>
                    {filteredPages.length === 0 ? (
                        <div style={{
                            textAlign: 'center',
                            padding: '40px',
                            color: '#9a9a9a',
                            fontSize: '14px'
                        }}>
                            No pages found matching your filters.
                        </div>
                    ) : (
                        filteredPages.map((page, index) => {
                            const isExpanded = expandedPages[index];
                            return (
                                <div
                                    key={index}
                                    style={{
                                        ...pageCardStyle,
                                        ...(isExpanded ? pageCardExpandedStyle : {})
                                    }}
                                    onClick={() => togglePage(index)}
                                    onMouseEnter={(e) => {
                                        if (!isExpanded) {
                                            e.currentTarget.style.border = '1px solid rgba(139, 92, 246, 0.4)';
                                        }
                                    }}
                                    onMouseLeave={(e) => {
                                        if (!isExpanded) {
                                            e.currentTarget.style.border = '1px solid rgba(139, 92, 246, 0.25)';
                                        }
                                    }}
                                >
                                    <div style={pageHeaderStyle}>
                                        <div style={{ flex: 1 }}>
                                            <h3 style={pageTitleStyle}>
                                                {page.page_name}
                                                {page.page_url && (
                                                    <span style={{ fontSize: '13px', color: '#9a9a9a', fontWeight: '400', marginLeft: '8px' }}>
                                                        ({page.page_url})
                                                    </span>
                                                )}
                                            </h3>
                                            {page.page_type && (
                                                <div style={{ fontSize: '12px', color: '#9a9a9a', marginTop: '4px' }}>
                                                    Type: {page.page_type}
                                                </div>
                                            )}
                                        </div>
                                        <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                                            {page.priority && (
                                                <span style={badgeStyle(page.priority)}>
                                                    {page.priority}
                                                </span>
                                            )}
                                            <span style={{ fontSize: '12px', color: '#9a9a9a' }}>
                                                {isExpanded ? '▼' : '▶'}
                                            </span>
                                        </div>
                                    </div>

                                    {page.purpose && !isExpanded && (
                                        <div style={{ fontSize: '13px', color: '#c0c0c0', marginTop: '8px' }}>
                                            {page.purpose.substring(0, 150)}{page.purpose.length > 150 ? '...' : ''}
                                        </div>
                                    )}

                                    {isExpanded && (
                                        <div style={{ marginTop: '16px' }}>
                                            {page.purpose && (
                                                <div style={subsectionStyle}>
                                                    <div style={labelStyle}>Purpose</div>
                                                    <div style={valueStyle}>{page.purpose}</div>
                                                </div>
                                            )}

                                            {page.target_audience && (
                                                <div style={subsectionStyle}>
                                                    <div style={labelStyle}>Target Audience</div>
                                                    <div style={valueStyle}>{page.target_audience}</div>
                                                </div>
                                            )}

                                            {page.content_overview && (
                                                <div style={subsectionStyle}>
                                                    <div style={labelStyle}>Content Overview</div>
                                                    <div style={valueStyle}>{page.content_overview}</div>
                                                </div>
                                            )}

                                            {page.key_sections && Array.isArray(page.key_sections) && page.key_sections.length > 0 && (
                                                <div style={subsectionStyle}>
                                                    <div style={labelStyle}>Key Sections ({page.key_sections.length})</div>
                                                    <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                                                        {page.key_sections.map((section, sectionIdx) => (
                                                            <div key={sectionIdx} style={{
                                                                backgroundColor: '#1f1f1f',
                                                                border: '1px solid #333333',
                                                                borderRadius: '6px',
                                                                padding: '10px',
                                                                marginBottom: '8px'
                                                            }}>
                                                                <div style={{ fontSize: '13px', fontWeight: '600', color: '#ffffff', marginBottom: '4px' }}>
                                                                    {section.section_name}
                                                                </div>
                                                                {section.section_purpose && (
                                                                    <div style={{ fontSize: '12px', color: '#9a9a9a', marginBottom: '6px' }}>
                                                                        {section.section_purpose}
                                                                    </div>
                                                                )}
                                                                {section.content_elements && Array.isArray(section.content_elements) && (
                                                                    <div>
                                                                        <div style={{ fontSize: '11px', color: '#6a6a6a', marginBottom: '4px' }}>Content Elements:</div>
                                                                        <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '12px', color: '#c0c0c0' }}>
                                                                            {section.content_elements.map((element, elemIdx) => (
                                                                                <li key={elemIdx}>{element}</li>
                                                                            ))}
                                                                        </ul>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}

                                            {page.calls_to_action && Array.isArray(page.calls_to_action) && page.calls_to_action.length > 0 && (
                                                <div style={subsectionStyle}>
                                                    <div style={labelStyle}>Calls to Action</div>
                                                    {page.calls_to_action.map((cta, ctaIdx) => (
                                                        <div key={ctaIdx} style={{
                                                            backgroundColor: '#1f1f1f',
                                                            border: '1px solid #333333',
                                                            borderRadius: '6px',
                                                            padding: '8px',
                                                            marginBottom: '6px'
                                                        }}>
                                                            <div style={{ fontSize: '13px', fontWeight: '600', color: '#ffffff' }}>
                                                                {cta.cta_text}
                                                            </div>
                                                            {cta.cta_purpose && (
                                                                <div style={{ fontSize: '12px', color: '#9a9a9a', marginTop: '4px' }}>
                                                                    {cta.cta_purpose}
                                                                </div>
                                                            )}
                                                            {cta.cta_location && (
                                                                <div style={{ fontSize: '11px', color: '#6a6a6a', marginTop: '4px' }}>
                                                                    Location: {cta.cta_location}
                                                                </div>
                                                            )}
                                                        </div>
                                                    ))}
                                                </div>
                                            )}

                                            {page.seo_requirements && (
                                                <div style={subsectionStyle}>
                                                    <div style={labelStyle}>SEO Requirements</div>
                                                    {page.seo_requirements.target_keywords && Array.isArray(page.seo_requirements.target_keywords) && (
                                                        <div style={{ marginBottom: '8px' }}>
                                                            <div style={{ fontSize: '12px', color: '#9a9a9a', marginBottom: '4px' }}>Target Keywords:</div>
                                                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                                                                {page.seo_requirements.target_keywords.map((keyword, kwIdx) => (
                                                                    <span key={kwIdx} style={{
                                                                        backgroundColor: '#2d2d2d',
                                                                        border: '1px solid #404040',
                                                                        borderRadius: '4px',
                                                                        padding: '4px 8px',
                                                                        fontSize: '11px',
                                                                        color: '#e0e0e0'
                                                                    }}>
                                                                        {keyword}
                                                                    </span>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    )}
                                                    {page.seo_requirements.meta_description && (
                                                        <div style={{ marginBottom: '8px' }}>
                                                            <div style={{ fontSize: '12px', color: '#9a9a9a', marginBottom: '4px' }}>Meta Description:</div>
                                                            <div style={{ fontSize: '13px', color: '#c0c0c0' }}>{page.seo_requirements.meta_description}</div>
                                                        </div>
                                                    )}
                                                    {page.seo_requirements.seo_title && (
                                                        <div>
                                                            <div style={{ fontSize: '12px', color: '#9a9a9a', marginBottom: '4px' }}>SEO Title:</div>
                                                            <div style={{ fontSize: '13px', color: '#c0c0c0' }}>{page.seo_requirements.seo_title}</div>
                                                        </div>
                                                    )}
                                                </div>
                                            )}

                                            {page.related_pages && Array.isArray(page.related_pages) && page.related_pages.length > 0 && (
                                                <div style={subsectionStyle}>
                                                    <div style={labelStyle}>Related Pages</div>
                                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                                                        {page.related_pages.map((relatedPage, rpIdx) => (
                                                            <span key={rpIdx} style={{
                                                                backgroundColor: '#2d2d2d',
                                                                border: '1px solid #404040',
                                                                borderRadius: '4px',
                                                                padding: '4px 8px',
                                                                fontSize: '12px',
                                                                color: '#e0e0e0'
                                                            }}>
                                                                {relatedPage}
                                                            </span>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}

                                            {page.notes && (
                                                <div style={subsectionStyle}>
                                                    <div style={labelStyle}>Notes</div>
                                                    <div style={valueStyle}>{page.notes}</div>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            );
                        })
                    )}
                </div>

                {/* Summary Recommendations */}
                {proposedPages.summary && proposedPages.summary.general_recommendations && Array.isArray(proposedPages.summary.general_recommendations) && (
                    <div style={{
                        marginTop: '30px',
                        padding: '20px',
                        backgroundColor: '#2d2d2d',
                        border: '1px solid #404040',
                        borderRadius: '12px'
                    }}>
                        <div style={{ fontSize: '16px', fontWeight: '600', color: '#ffffff', marginBottom: '12px' }}>
                            General Recommendations
                        </div>
                        <ul style={{ margin: 0, paddingLeft: '20px', color: '#e0e0e0' }}>
                            {proposedPages.summary.general_recommendations.map((rec, idx) => (
                                <li key={idx} style={{ marginBottom: '8px', lineHeight: '1.6' }}>
                                    {rec}
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ProposedPagesModal;

